import React from 'react';
import { Box, useMediaQuery } from '@mui/material';
import SectionHead from './SectionHead';
import EvidenceRing from './EvidenceRing';
import LandingButton from './LandingButton';
import Reveal from './Reveal';
import useInView from './useInView';
import { BP, LANDING_FONT, LC, MAXW, MOTION, PAD_X, SECTION_Y, r } from './landingTokens';

// ─── How it works — horizontal 3-card layout (per the landing mock) ──────────
// Cards sit side by side on desktop (2-up tablet, stacked phone) and cascade in
// with the unified stagger. Replaces the vertical scrollytelling, which cost
// ~1.7 viewport-heights of scroll for one section.
const stepSx = {
  width: '100%',
  background: LC.surface,
  border: `${r(1)} solid ${LC.border}`,
  borderRadius: r(20),
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
  '&:hover': {
    transform: 'translateY(-0.1875rem)',
    boxShadow: '0 0.75rem 1.75rem -0.75rem rgba(12,31,23,.22)',
    borderColor: LC.evergreen,
  },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
};
const stepTopSx = { px: r(26), pt: r(24), [BP.desktop]: { minHeight: r(235) } };
const numSx = { width: r(30), height: r(30), borderRadius: r(9), background: LC.surfaceTint, display: 'grid', placeItems: 'center', fontFamily: LANDING_FONT, fontWeight: 900, fontSize: r(14), color: LC.evergreen, flex: 'none' };
const kickerSx = { fontFamily: LANDING_FONT, fontSize: r(11), fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: LC.inkMuted };
const titleSx = { fontFamily: LANDING_FONT, fontWeight: 800, fontSize: r(20), letterSpacing: '-0.02em', mt: r(16), color: LC.ink };
const descSx = { fontFamily: LANDING_FONT, fontSize: r(14), color: LC.inkSoft, lineHeight: 1.55, mt: r(9) };
const demoSx = { m: `${r(22)} ${r(26)} ${r(26)}`, [BP.desktop]: { mt: 0 }, background: LC.surfaceAlt, border: `${r(1)} solid ${LC.borderSoft}`, borderRadius: r(14), p: r(14), display: 'flex', flexDirection: 'column', gap: r(9) };
const studyLink = { fontFamily: LANDING_FONT, fontWeight: 600, fontSize: r(11), color: LC.accent, textDecoration: 'none', '&:hover': { color: LC.linkHover } };

// Two-act choreography: the card shells enter EXACTLY like the Go-deeper cards
// (act 1 — section-tier rise, 70ms ripple; the perceivable movement), then the
// demo content inside plays left→right as a bonus once the entrances complete
// (act 2 — see demoBase/demoStep below).
const Step = ({ num, kicker, title, desc, children }) => (
  <Reveal delay={(Number(num) - 1) * MOTION.stagger} repeat sx={{ display: 'flex' }}>
    <Box sx={stepSx}>
      <Box sx={stepTopSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: r(10) }}>
          <Box sx={numSx}>{num}</Box>
          <Box sx={kickerSx}>{kicker}</Box>
        </Box>
        <Box sx={titleSx}>{title}</Box>
        <Box sx={descSx}>{desc}</Box>
      </Box>
      <Box sx={demoSx}>{children}</Box>
    </Box>
  </Reveal>
);

// Content is NEVER hidden: every text element displays with its card. Only the
// genuinely animateable data elements play afterward — ring sweeps and SVG
// stroke-draws — none of which obscure content. On desktop, `shown`/`instant`
// are driven by ONE section-level observer (per-element observers mis-order
// the chain: the demo panels bottom-align, so shorter panels cross the trigger
// line later than their right-hand neighbors). When `shown` is undefined
// (mobile, stacked cards), each element falls back to its own observer.
const DemoRow = ({ name, link, value, pct, dim, delay = 0, shown, instant }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: LC.surface, border: `${r(1)} solid ${LC.border}`, borderRadius: r(10), p: `${r(10)} ${r(12)}`, opacity: dim ? 0.55 : 1 }}>
    {link ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: r(3) }}>
        <Box component="span" sx={{ fontFamily: LANDING_FONT, fontSize: r(12.5), fontWeight: 700, color: LC.ink }}>{name}</Box>
        <Box component="a" href="#" sx={studyLink}>{link}</Box>
      </Box>
    ) : (
      <Box component="span" sx={{ fontFamily: LANDING_FONT, fontSize: r(12.5), fontWeight: 700, color: LC.ink }}>{name}</Box>
    )}
    <EvidenceRing value={value} pct={pct} size="sm" animate delay={delay} active={shown} activeInstant={instant} />
  </Box>
);

// The goal pill's arrow stroke-draws — card 1's animateable moment.
const ArrowDraw = ({ shown, instant, delay = 0 }) => {
  const [ref, hookShown, hookInstant] = useInView();
  const controlled = shown !== undefined;
  const isShown = controlled ? shown : hookShown;
  const isInstant = controlled ? !!instant : hookInstant;
  return (
    <Box ref={controlled ? undefined : ref} component="svg" width={15} height={15} viewBox="0 0 20 20" aria-hidden="true" sx={{ flex: 'none' }}>
      <Box
        component="path"
        d="M4 10h11M11 5.5l4.5 4.5L11 14.5"
        pathLength="1"
        fill="none"
        stroke={LC.accent}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        sx={{
          strokeDasharray: 1,
          strokeDashoffset: isShown ? 0 : 1,
          transition: isInstant ? 'none' : `stroke-dashoffset ${MOTION.durationSm}ms ${MOTION.ease} ${delay}ms`,
          '@media (prefers-reduced-motion: reduce)': { strokeDashoffset: 0, transition: 'none' },
        }}
      />
    </Box>
  );
};

// "Screened" check stroke-draws with its card's moment — verification resolving.
const ScreenedCheck = ({ shown, instant, delay = 0 }) => {
  const [ref, hookShown, hookInstant] = useInView();
  const controlled = shown !== undefined;
  const isShown = controlled ? shown : hookShown;
  const isInstant = controlled ? !!instant : hookInstant;
  return (
    <Box ref={controlled ? undefined : ref} component="svg" width={14} height={14} viewBox="0 0 14 14" aria-hidden="true" sx={{ flex: 'none' }}>
      <Box
        component="path"
        d="M3 7.4l2.6 2.6L11 4.6"
        pathLength="1"
        fill="none"
        stroke={LC.posInk}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        sx={{
          strokeDasharray: 1,
          strokeDashoffset: isShown ? 0 : 1,
          transition: isInstant ? 'none' : `stroke-dashoffset ${MOTION.durationSm}ms ${MOTION.ease} ${delay}ms`,
          '@media (prefers-reduced-motion: reduce)': { strokeDashoffset: 0, transition: 'none' },
        }}
      />
    </Box>
  );
};

export default function HowItWorks() {
  // Act 2 timing: once the card entrances complete (cards ripple at
  // MOTION.stagger offsets and run MOTION.duration; the ease-out has them
  // visually settled by ~1s), the VISUAL data elements animate left→right —
  // arrow draw (card 1) → ring sweeps (card 2) → check draw (card 3). Text is
  // never hidden. One section-level observer on the grid drives all of them
  // (per-element observers mis-order the chain: demo panels bottom-align, so
  // shorter panels cross the trigger line later than their neighbors). When
  // stacked (mobile), `shown` stays undefined and each element self-triggers.
  const isRow = useMediaQuery(BP.desktop);
  const demoBase = isRow ? 1000 : 0;
  const demoStep = isRow ? 300 : 0;
  const [gridRef, gridInView, gridInstant] = useInView();
  const demoShown = isRow ? gridInView : undefined;
  const demoInstant = isRow ? gridInstant : undefined;

  return (
    <Box component="section" id="how" sx={{ background: LC.surface, borderBottom: `${r(1)} solid ${LC.border}`, display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: MAXW, px: PAD_X, py: SECTION_Y }}>
        <Reveal repeat>
          <SectionHead
            eyebrow="How it works"
            title="One goal in. One trusted stack out."
            lead="Three steps. The hard part — vetting the research — is the part we do for you."
            maxWidth={r(640)}
            leadMaxWidth="52ch"
          />
        </Reveal>

        <Box ref={gridRef} sx={{ display: 'grid', gridTemplateColumns: '1fr', [BP.tablet]: { gridTemplateColumns: 'repeat(2, 1fr)' }, [BP.desktop]: { gridTemplateColumns: 'repeat(3, 1fr)' }, gap: r(18), mt: 'clamp(2.125rem, 4vw, 3.25rem)' }}>
          <Step num="1" kicker="Tell us" title="Your goal, plus what you already take and your diet" desc="Type it in plain words once — that’s all it takes to match a stack to your health profile.">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: r(9), background: LC.surface, border: `${r(1)} solid ${LC.border}`, borderRadius: r(10), p: `${r(11)} ${r(13)}` }}>
              <ArrowDraw shown={demoShown} instant={demoInstant} delay={demoBase} />
              <Box component="span" sx={{ fontFamily: LANDING_FONT, fontSize: r(13), fontWeight: 600, color: LC.ink }}>Deeper sleep</Box>
            </Box>
            <Box sx={{ display: 'flex', gap: r(7), flexWrap: 'wrap' }}>
              <Box component="span" sx={{ fontFamily: LANDING_FONT, fontSize: r(11), fontWeight: 600, color: LC.evergreen, background: LC.surface, border: `${r(1)} solid ${LC.border}`, borderRadius: r(999), p: `${r(5)} ${r(10)}` }}>Metformin</Box>
            </Box>
          </Step>

          <Step num="2" kicker="We read" title="Thousands of human studies, vetted." desc="See study strength and fit behind every recommendation — in plain language, with nothing hidden.">
            <DemoRow name="L-theanine" link="View 2 studies →" value="5" pct={100} delay={demoBase + demoStep} shown={demoShown} instant={demoInstant} />
            <DemoRow name="Magnesium glycinate" link="View 1 study →" value="4" pct={80} delay={demoBase + demoStep} shown={demoShown} instant={demoInstant} />
            <DemoRow name="Glycine" value="3" pct={60} dim delay={demoBase + demoStep} shown={demoShown} instant={demoInstant} />
          </Step>

          <Step num="3" kicker="You own it" title="Yours to keep, share, and adjust anytime" desc="Your stack lives in the app — with third-party-tested product options, and any interactions flagged for you to review with your provider.">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: r(9), borderRadius: r(10), p: `${r(10)} ${r(12)}`, background: LC.posBg, border: `${r(1)} solid ${LC.posBorder}` }}>
              <ScreenedCheck shown={demoShown} instant={demoInstant} delay={demoBase + demoStep * 2} />
              <Box component="span" sx={{ fontFamily: LANDING_FONT, fontSize: r(12), fontWeight: 600, color: LC.posInk }}>Screened — no conflicts found</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: r(9), borderRadius: r(10), p: `${r(10)} ${r(12)}`, background: LC.surface, border: `${r(1)} solid ${LC.border}` }}>
              <Box component="svg" width={14} height={14} viewBox="0 0 14 14" aria-hidden="true" sx={{ flex: 'none' }}>
                <path d="M2 3.2h1.4l1.1 6.2h5.5l1.1-4.4H4.2" fill="none" stroke={LC.accent} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="6" cy="11.6" r="0.9" fill={LC.accent} />
                <circle cx="10" cy="11.6" r="0.9" fill={LC.accent} />
              </Box>
              <Box component="span" sx={{ fontFamily: LANDING_FONT, fontSize: r(12), fontWeight: 600, color: LC.ink }}>View Products</Box>
            </Box>
          </Step>
        </Box>

        <Box sx={{ mt: r(36), textAlign: 'center' }}>
          <LandingButton href="#top" sx={{ fontSize: r(16), px: r(28), py: r(15), borderRadius: r(12) }}>Create your stack</LandingButton>
        </Box>
      </Box>
    </Box>
  );
}
